import { useEffect, useState } from "react";
import { fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import { CalendarDays, Save, Loader2, Plus, Trash2, Type, RotateCcw, Languages } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { LATIN_SERIF, LATIN_SANS, DEVANAGARI } from "@/components/TypographyLoader";
import { PF_GROUPS } from "@/lib/panchangRoles";
import { PANCHANG_TA_DEFAULTS, PANCHANG_EN_DEFAULTS, TA_ADMIN_GROUPS, TA_UI, TA_FONTS, TE_FONTS, fieldDefault } from "@/lib/panchangTamil";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const FONT_OPTIONS = [...LATIN_SERIF, ...LATIN_SANS, ...DEVANAGARI];
const PA_EDIT_LANGS = [{ code: "en", label: "English" }, { code: "hi", label: "हिन्दी", cls: "font-deva-body" }, { code: "ta", label: "தமிழ்", cls: "font-tamil-body" }, { code: "te", label: "తెలుగు", cls: "font-telugu-body" }];

export default function AdminPanchang() {
  const [notes, setNotes] = useState(null);
  const [sections, setSections] = useState([]);
  const [fonts, setFonts] = useState({});
  const [tamil, setTamil] = useState({});
  const [wordRows, setWordRows] = useState([]);
  const [langFonts, setLangFonts] = useState({});
  const [glossary, setGlossary] = useState({ enabled: true, heading_en: "Panchangam Glossary", heading_hi: "पंचांग शब्दावली", terms: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdminSettings().then((d) => {
      setNotes(d.panchang_notes || []);
      setSections(d.panchang_sections || []);
      setFonts(d.panchang_fonts || {});
      const { words: savedWords, ...restTamil } = d.panchang_tamil || {};
      setTamil(restTamil);
      setWordRows(Object.entries(savedWords || {}).map(([src, v]) => ({ src, ...(typeof v === "string" ? { ta: v } : (v || {})) })));
      setLangFonts(d.panchang_lang_fonts || {});
      const g = d.panchang_glossary || {};
      setGlossary({ enabled: g.enabled !== false, heading_en: g.heading_en || "Panchangam Glossary", heading_hi: g.heading_hi || "पंचांग शब्दावली", sub_en: g.sub_en || "", sub_hi: g.sub_hi || "", terms: g.terms || [] });
    }).catch(() => { setNotes([]); setSections([]); });
  }, []);

  const setNote = (i, k, v) => setNotes((n) => n.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addNote = () => setNotes((n) => [...n, { date: "", time: "", en: "", hi: "", note_en: "", note_hi: "", image: "" }]);
  const delNote = (i) => setNotes((n) => n.filter((_, idx) => idx !== i));

  const setSec = (i, k, v) => setSections((s) => s.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addSec = () => setSections((s) => [...s, { title_en: "", title_hi: "", body_en: "", body_hi: "" }]);
  const delSec = (i) => setSections((s) => s.filter((_, idx) => idx !== i));

  // Panchangam typography roles — each stored as { font, size, color }.
  const roleCfg = (key) => {
    const v = fonts[key];
    return typeof v === "string" ? { font: v, size: "", color: "" } : { font: "", size: "", color: "", ...(v || {}) };
  };
  const setRole = (key, prop, val) =>
    setFonts((p) => ({ ...p, [key]: { ...roleCfgFrom(p[key]), [prop]: val } }));
  const roleCfgFrom = (v) => (typeof v === "string" ? { font: v, size: "", color: "" } : { font: "", size: "", color: "", ...(v || {}) });
  const resetRole = (key) => setFonts((p) => { const n = { ...p }; delete n[key]; return n; });

  // Per-field translation overrides (English / Hindi / Tamil). Stored as
  // { cat: { key: { en?, hi?, ta? } } }; a legacy plain string = Tamil.
  const cellObj = (cat, key) => { const v = tamil[cat] && tamil[cat][key]; return v == null ? {} : (typeof v === "string" ? { ta: v } : v); };
  const cellVal = (cat, key, lang) => { const c = cellObj(cat, key); return lang in c ? c[lang] : ""; };
  const cellDirty = (cat, key, lang) => { const c = cellObj(cat, key); return lang in c && c[lang] !== ""; };
  const setCell = (cat, key, lang, val) => setTamil((p) => {
    const prev = p[cat] && p[cat][key]; const base = typeof prev === "string" ? { ta: prev } : { ...(prev || {}) };
    base[lang] = val;
    return { ...p, [cat]: { ...(p[cat] || {}), [key]: base } };
  });
  const resetCell = (cat, key, lang) => setTamil((p) => {
    const prev = p[cat] && p[cat][key]; const base = typeof prev === "string" ? { ta: prev } : { ...(prev || {}) };
    delete base[lang];
    const catObj = { ...(p[cat] || {}) };
    if (Object.keys(base).length) catObj[key] = base; else delete catObj[key];
    const n = { ...p }; if (Object.keys(catObj).length) n[cat] = catObj; else delete n[cat];
    return n;
  });
  const setLangFont = (lang, val) => setLangFonts((p) => { const n = { ...p }; if (val) n[lang] = val; else delete n[lang]; return n; });

  // Custom word mappings — admin maps ANY word (deity names, epithets, etc.) that
  // the panchang shows, per language; the page fetches from here.
  const addWord = () => setWordRows((w) => [...w, { src: "", en: "", hi: "", ta: "", te: "" }]);
  const setWord = (i, k, v) => setWordRows((w) => w.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const delWord = (i) => setWordRows((w) => w.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const cleanNotes = (notes || []).filter((n) => n.date && (n.en || n.hi));
      const cleanSecs = (sections || []).filter((s) => (s.title_en || s.title_hi) && (s.body_en || s.body_hi));
      // Persist only values that differ from the built-in default per language.
      const cleanTamil = {};
      for (const [cat, obj] of Object.entries(tamil)) {
        for (const [k, raw] of Object.entries(obj || {})) {
          const c = typeof raw === "string" ? { ta: raw } : (raw || {});
          const kept = {};
          for (const lg of ["en", "hi", "ta", "te"]) {
            const v = c[lg];
            if (v != null && v !== "" && v !== fieldDefault(cat, k, lg)) kept[lg] = v;
          }
          if (Object.keys(kept).length) { (cleanTamil[cat] = cleanTamil[cat] || {})[k] = kept; }
        }
      }
      // Custom word mappings (admin can map ANY word per language).
      const cleanWords = {};
      for (const r of wordRows) {
        const src = (r.src || "").trim();
        if (!src) continue;
        const kept = {};
        for (const lg of ["en", "hi", "ta", "te"]) { if (r[lg] && String(r[lg]).trim()) kept[lg] = String(r[lg]).trim(); }
        if (Object.keys(kept).length) cleanWords[src] = kept;
      }
      if (Object.keys(cleanWords).length) cleanTamil.words = cleanWords;
      const cleanGlossary = {
        enabled: glossary.enabled !== false,
        heading_en: glossary.heading_en || "", heading_hi: glossary.heading_hi || "",
        sub_en: glossary.sub_en || "", sub_hi: glossary.sub_hi || "",
        terms: (glossary.terms || []).filter((t) => t && (t.term_en || t.term_hi)).map((t) => ({
          id: t.id || `g_${Math.random().toString(36).slice(2, 8)}`,
          term_en: t.term_en || "", term_hi: t.term_hi || "", term_ta: t.term_ta || "", term_te: t.term_te || "",
          def_en: t.def_en || "", def_hi: t.def_hi || "", def_ta: t.def_ta || "", def_te: t.def_te || "",
          hidden: !!t.hidden,
        })),
      };
      const updated = await adminUpdateSettings({ panchang_notes: cleanNotes, panchang_sections: cleanSecs, panchang_fonts: fonts, panchang_tamil: cleanTamil, panchang_lang_fonts: langFonts, panchang_glossary: cleanGlossary });
      setNotes(updated.panchang_notes || []);
      setSections(updated.panchang_sections || []);
      setFonts(updated.panchang_fonts || {});
      const { words: uWords, ...uRest } = updated.panchang_tamil || {};
      setTamil(uRest);
      setWordRows(Object.entries(uWords || {}).map(([src, v]) => ({ src, ...(typeof v === "string" ? { ta: v } : (v || {})) })));
      setLangFonts(updated.panchang_lang_fonts || {});
      if (updated.panchang_glossary) setGlossary({ enabled: updated.panchang_glossary.enabled !== false, heading_en: updated.panchang_glossary.heading_en || "", heading_hi: updated.panchang_glossary.heading_hi || "", sub_en: updated.panchang_glossary.sub_en || "", sub_hi: updated.panchang_glossary.sub_hi || "", terms: updated.panchang_glossary.terms || [] });
      toast("Panchangam settings saved", { description: "Republish (Sync) to update the live site." });
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (notes === null) return <p className="py-20 text-center text-ink-3">…</p>;

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-panchang">
      <div className="mb-2 flex items-center gap-2.5">
        <CalendarDays className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Panchangam Editor</h1>
      </div>
      <p className="mb-8 max-w-2xl text-sm text-ink-3">
        Highlight special festival / vrat dates with a note that appears on the Panchangam page, and add your own
        custom content sections shown below the daily almanac.
      </p>

      {/* Special dates */}
      <section className="border border-line bg-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif-latin text-xl text-ink">Special dates</h2>
          <button onClick={addNote} data-testid="panchang-add-note" className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-sm text-ink-2 hover:border-saffron hover:text-saffron">
            <Plus className="h-4 w-4" /> Add date
          </button>
        </div>
        {notes.length === 0 ? (
          <p className="text-sm text-ink-3">No special dates yet.</p>
        ) : (
          <div className="space-y-4">
            {notes.map((n, i) => (
              <div key={i} className="border border-line p-4" data-testid={`panchang-note-${i}`}>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Date</label>
                    <input type="date" value={n.date || ""} onChange={(e) => setNote(i, "date", e.target.value)} className={inp} data-testid={`panchang-note-date-${i}`} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Time (optional)</label>
                    <input type="time" value={n.time || ""} onChange={(e) => setNote(i, "time", e.target.value)} className={inp} data-testid={`panchang-note-time-${i}`} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Title (English)</label>
                    <input value={n.en || ""} onChange={(e) => setNote(i, "en", e.target.value)} placeholder="e.g. Ganesh Chaturthi" className={inp} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">शीर्षक (Hindi)</label>
                    <input value={n.hi || ""} onChange={(e) => setNote(i, "hi", e.target.value)} placeholder="जैसे गणेश चतुर्थी" className={`${inp} font-deva-body`} />
                  </div>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Note — English (optional)</label>
                    <textarea value={n.note_en ?? n.note ?? ""} onChange={(e) => setNote(i, "note_en", e.target.value)} rows={3} placeholder="Short description (English)" className={inp} data-testid={`panchang-note-en-${i}`} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">टिप्पणी — हिन्दी (वैकल्पिक)</label>
                    <textarea value={n.note_hi || ""} onChange={(e) => setNote(i, "note_hi", e.target.value)} rows={3} placeholder="संक्षिप्त विवरण (हिन्दी)" className={`${inp} font-deva-body`} data-testid={`panchang-note-hi-${i}`} />
                  </div>
                </div>
                <div className="mt-3">
                  <ImageUploader label="Image (optional)" testid={`panchang-note-image-${i}`} value={n.image || ""} onChange={(v) => setNote(i, "image", v)} hint="Shown beside the date on the Panchangam page" />
                </div>
                <button onClick={() => delNote(i)} className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--saffron-dark)] hover:underline">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Custom sections */}
      <section className="mt-6 border border-line bg-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif-latin text-xl text-ink">Custom sections</h2>
          <button onClick={addSec} data-testid="panchang-add-section" className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-sm text-ink-2 hover:border-saffron hover:text-saffron">
            <Plus className="h-4 w-4" /> Add section
          </button>
        </div>
        {sections.length === 0 ? (
          <p className="text-sm text-ink-3">No custom sections yet.</p>
        ) : (
          <div className="space-y-4">
            {sections.map((s, i) => (
              <div key={i} className="border border-line p-4" data-testid={`panchang-section-${i}`}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input value={s.title_en || ""} onChange={(e) => setSec(i, "title_en", e.target.value)} placeholder="Section title (English)" className={inp} />
                  <input value={s.title_hi || ""} onChange={(e) => setSec(i, "title_hi", e.target.value)} placeholder="अनुभाग शीर्षक (Hindi)" className={`${inp} font-deva-body`} />
                  <textarea value={s.body_en || ""} onChange={(e) => setSec(i, "body_en", e.target.value)} rows={3} placeholder="Content (English)" className={inp} />
                  <textarea value={s.body_hi || ""} onChange={(e) => setSec(i, "body_hi", e.target.value)} rows={3} placeholder="सामग्री (Hindi)" className={`${inp} font-deva-body`} />
                </div>
                <button onClick={() => delSec(i)} className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--saffron-dark)] hover:underline">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Typography & Style */}
      <section className="mt-6 border border-line bg-surface p-6" data-testid="panchang-fonts">
        <div className="mb-1 flex items-center gap-2">
          <Type className="h-5 w-5 text-saffron" />
          <h2 className="font-serif-latin text-xl text-ink">Typography &amp; Style</h2>
        </div>
        <p className="mb-5 text-sm text-ink-3">
          Control the <strong>font, size and colour</strong> of every text element on the Panchangam page and the homepage
          Panchangam strip. Leave a control blank to keep the default. Cards share one uniform default size so they read
          consistently — change any role here to override it.
        </p>
        <div className="space-y-6">
          {PF_GROUPS.map((g) => (
            <div key={g.group}>
              <p className="overline-label mb-2 text-gold">{g.group}</p>
              {g.roles.some((r) => r.numbers) && (
                <p className="mb-2 text-xs text-[var(--saffron-dark)]">
                  ⚠ Times &amp; numerals use a clean lining-numeral font by default. Some decorative fonts render
                  old-style figures (e.g. uneven digit heights) — pick a sans font if the times look off.
                </p>
              )}
              <div className="space-y-2">
                {g.roles.map((r) => {
                  const c = roleCfg(r.key);
                  const dirty = c.font || c.size || c.color;
                  return (
                    <div key={r.key} className="grid items-center gap-2 border border-line/70 p-2.5 sm:grid-cols-[1.3fr_1.4fr_0.8fr_0.9fr_auto]" data-testid={`pf-role-${r.key}`}>
                      <span className="text-sm text-ink-2">{r.label}</span>
                      <select data-testid={`pf-${r.key}-font`} value={c.font || ""} onChange={(e) => setRole(r.key, "font", e.target.value)}
                        className={inp} style={c.font ? { fontFamily: `'${c.font}', inherit` } : undefined}>
                        <option value="">Default font</option>
                        {FONT_OPTIONS.map((name) => <option key={name} value={name}>{name}</option>)}
                      </select>
                      <div className="flex items-center gap-1">
                        <input type="number" min="8" max="80" step="1" placeholder="px" data-testid={`pf-${r.key}-size`}
                          value={c.size || ""} onChange={(e) => setRole(r.key, "size", e.target.value)} className={inp} />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <input type="color" data-testid={`pf-${r.key}-color`} value={c.color || "#3a2a1a"}
                          onChange={(e) => setRole(r.key, "color", e.target.value)}
                          className="h-9 w-10 shrink-0 cursor-pointer border border-line bg-parchment" />
                        {c.color && <button onClick={() => setRole(r.key, "color", "")} className="text-[11px] text-ink-3 hover:text-saffron">clear</button>}
                      </div>
                      <button onClick={() => resetRole(r.key)} disabled={!dirty} title="Reset to default"
                        className="justify-self-end text-ink-3 transition-colors hover:text-saffron disabled:opacity-30">
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Field translations (English / Hindi / Tamil) + script fonts */}
      <section className="mt-6 border border-line bg-surface p-6" data-testid="panchang-tamil">
        <div className="mb-1 flex items-center gap-2">
          <Languages className="h-5 w-5 text-saffron" />
          <h2 className="font-serif-latin text-xl text-ink">Field translations &amp; fonts</h2>
        </div>
        <p className="mb-5 text-sm text-ink-3">
          Edit any term or label shown on the Panchangam page in <strong>English, हिन्दी and தமிழ்</strong>.
          Blank uses the built-in default. Numbers &amp; times are never translated.
        </p>

        {/* Script font pickers */}
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Tamil font (தமிழ்)</label>
            <select data-testid="panchang-ta-font" value={langFonts.ta || ""} onChange={(e) => setLangFont("ta", e.target.value)} className={inp} style={langFonts.ta ? { fontFamily: `'${langFonts.ta}', serif` } : undefined}>
              <option value="">Default (Noto Serif Tamil)</option>
              {TA_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Telugu font (తెలుగు)</label>
            <select data-testid="panchang-te-font" value={langFonts.te || ""} onChange={(e) => setLangFont("te", e.target.value)} className={inp} style={langFonts.te ? { fontFamily: `'${langFonts.te}', serif` } : undefined}>
              <option value="">Default (Noto Serif Telugu)</option>
              {TE_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Hindi font (हिन्दी)</label>
            <select data-testid="panchang-hi-font" value={langFonts.hi || ""} onChange={(e) => setLangFont("hi", e.target.value)} className={inp} style={langFonts.hi ? { fontFamily: `'${langFonts.hi}', serif` } : undefined}>
              <option value="">Default (Noto Serif Devanagari)</option>
              {DEVANAGARI.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-3">
          {TA_ADMIN_GROUPS.map((g) => {
            const keys = g.cat === "ui" ? Object.keys(PANCHANG_EN_DEFAULTS.ui) : Object.keys(PANCHANG_TA_DEFAULTS[g.cat] || {});
            return (
              <details key={g.cat} className="border border-line/70" data-testid={`ta-group-${g.cat}`}>
                <summary className="cursor-pointer select-none bg-parchment px-4 py-2.5 text-sm font-medium text-ink">
                  {g.label} <span className="text-ink-3">· {keys.length}</span>
                </summary>
                <div className="space-y-3 p-3">
                  {keys.map((key) => (
                    <div key={key} className="border border-line/50 p-2.5" data-testid={`ta-row-${g.cat}-${key}`}>
                      <p className="mb-1.5 text-xs font-medium text-ink-2">{g.cat === "ui" ? (TA_UI[key]?.en || key) : key}</p>
                      <div className="grid gap-2 sm:grid-cols-3">
                        {PA_EDIT_LANGS.map((l) => (
                          <div key={l.code} className="flex items-center gap-1">
                            <input
                              value={cellVal(g.cat, key, l.code)}
                              onChange={(e) => setCell(g.cat, key, l.code, e.target.value)}
                              data-testid={`ta-input-${g.cat}-${key}-${l.code}`}
                              className={`${inp} ${l.cls || ""}`}
                              placeholder={fieldDefault(g.cat, key, l.code) || l.label}
                            />
                            <button onClick={() => resetCell(g.cat, key, l.code)} disabled={!cellDirty(g.cat, key, l.code)} title={`Reset ${l.label}`}
                              className="shrink-0 text-ink-3 transition-colors hover:text-saffron disabled:opacity-30">
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            );
          })}
        </div>
      </section>

      {/* Custom word mappings — map ANY panchang word per language */}
      <section className="mt-6 border border-line bg-surface p-6" data-testid="panchang-words">
        <div className="mb-1 flex items-center gap-2">
          <Languages className="h-5 w-5 text-saffron" />
          <h2 className="font-serif-latin text-xl text-ink">Custom word mappings</h2>
        </div>
        <p className="mb-5 max-w-2xl text-sm text-ink-3">
          Map <strong>any</strong> word that appears in the daily panchang (deity names, epithets, planets, etc.) to your
          preferred wording per language. Type the exact <strong>source word</strong> as it appears in English (e.g.
          <em> Indrani</em>, <em>Ahirbudhnya</em>, <em>Prajapati</em>) and its translations. These override the built-in
          maps and the page fetches from here — so nothing is left in English.
        </p>
        <div className="mb-3 hidden gap-2 px-1 text-xs uppercase tracking-wider text-ink-3 sm:grid sm:grid-cols-[1.2fr_1fr_1fr_1fr_auto]">
          <span>Source word (English)</span><span>English</span><span>हिन्दी</span><span>தமிழ்</span><span></span>
        </div>
        <div className="space-y-2">
          {wordRows.length === 0 && <p className="text-sm text-ink-3">No custom words yet.</p>}
          {wordRows.map((r, i) => (
            <div key={i} className="grid items-center gap-2 sm:grid-cols-[1.2fr_1fr_1fr_1fr_auto]" data-testid={`pa-word-${i}`}>
              <input value={r.src || ""} onChange={(e) => setWord(i, "src", e.target.value)} placeholder="e.g. Indrani" className={inp} data-testid={`pa-word-src-${i}`} />
              <input value={r.en || ""} onChange={(e) => setWord(i, "en", e.target.value)} placeholder="English" className={inp} data-testid={`pa-word-en-${i}`} />
              <input value={r.hi || ""} onChange={(e) => setWord(i, "hi", e.target.value)} placeholder="हिन्दी" className={`${inp} font-deva-body`} data-testid={`pa-word-hi-${i}`} />
              <input value={r.ta || ""} onChange={(e) => setWord(i, "ta", e.target.value)} placeholder="தமிழ்" className={`${inp} font-tamil-body`} data-testid={`pa-word-ta-${i}`} />
              <div className="flex items-center gap-2">
                <input value={r.te || ""} onChange={(e) => setWord(i, "te", e.target.value)} placeholder="తెలుగు" className={`${inp} font-telugu-body`} data-testid={`pa-word-te-${i}`} />
                <button onClick={() => delWord(i)} title="Remove" className="shrink-0 text-ink-3 hover:text-red-600" data-testid={`pa-word-del-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addWord} data-testid="pa-word-add" className="mt-4 inline-flex items-center gap-2 border border-line px-3 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron">
          <Plus className="h-4 w-4" /> Add word mapping
        </button>
      </section>

      {/* Glossary */}
      <section className="mt-6 border border-line bg-surface p-6" data-testid="panchang-glossary-editor">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Languages className="h-5 w-5 text-saffron" />
            <h2 className="font-serif-latin text-xl text-ink">Panchangam Glossary</h2>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-2">
            <input type="checkbox" checked={glossary.enabled !== false} data-testid="glossary-enabled" onChange={(e) => setGlossary((g) => ({ ...g, enabled: e.target.checked }))} />
            Show glossary section on the Panchangam page
          </label>
        </div>
        <p className="mb-4 text-sm text-ink-3">Explain Panchangam terminology (Tithi, Nakshatra, Yoga, Rahu Kalam…). Each term shows in the reader's chosen language, falling back to English. Add, edit, hide or delete terms below.</p>

        <div className="mb-5 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Heading (English)</label>
            <input className={inp} value={glossary.heading_en || ""} data-testid="glossary-heading-en" onChange={(e) => setGlossary((g) => ({ ...g, heading_en: e.target.value }))} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Heading (Hindi)</label>
            <input className={`${inp} font-deva-body`} value={glossary.heading_hi || ""} data-testid="glossary-heading-hi" onChange={(e) => setGlossary((g) => ({ ...g, heading_hi: e.target.value }))} />
          </div>
        </div>

        <div className="space-y-4">
          {(glossary.terms || []).map((t, i) => (
            <div key={t.id || i} className={`border border-line p-3 ${t.hidden ? "opacity-60" : ""}`} data-testid={`glossary-term-${i}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-ink-3">Term {i + 1}</span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-ink-2"><input type="checkbox" checked={!!t.hidden} data-testid={`glossary-hide-${i}`} onChange={(e) => setGlossary((g) => ({ ...g, terms: g.terms.map((x, j) => j === i ? { ...x, hidden: e.target.checked } : x) }))} /> Hidden</label>
                  <button type="button" data-testid={`glossary-delete-${i}`} onClick={() => setGlossary((g) => ({ ...g, terms: g.terms.filter((_, j) => j !== i) }))} className="text-ink-3 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {PA_EDIT_LANGS.map((l) => (
                  <input key={l.code} className={`${inp} ${l.cls || ""}`} placeholder={`Term · ${l.label}`} data-testid={`glossary-term-${i}-${l.code}`} value={t[`term_${l.code}`] || ""} onChange={(e) => setGlossary((g) => ({ ...g, terms: g.terms.map((x, j) => j === i ? { ...x, [`term_${l.code}`]: e.target.value } : x) }))} />
                ))}
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {PA_EDIT_LANGS.map((l) => (
                  <textarea key={l.code} rows={2} className={`${inp} ${l.cls || ""}`} placeholder={`Definition · ${l.label}`} data-testid={`glossary-def-${i}-${l.code}`} value={t[`def_${l.code}`] || ""} onChange={(e) => setGlossary((g) => ({ ...g, terms: g.terms.map((x, j) => j === i ? { ...x, [`def_${l.code}`]: e.target.value } : x) }))} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <button type="button" data-testid="glossary-add" onClick={() => setGlossary((g) => ({ ...g, terms: [...(g.terms || []), { id: `g_${Math.random().toString(36).slice(2, 8)}` }] }))} className="mt-4 inline-flex items-center gap-2 border border-line px-3 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron">
          <Plus className="h-4 w-4" /> Add glossary term
        </button>
      </section>

      <div className="sticky bottom-4 mt-6">
        <button onClick={save} disabled={saving} data-testid="panchang-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-7 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Panchangam settings
        </button>
      </div>
    </div>
  );
}
